export const updatePagedList = <TList extends { data: unknown[]; }>(
  oldData: TList | undefined,
  predicate: (item: TList['data'][number]) => boolean,
  updater: (item: TList['data'][number]) => TList['data'][number]
): TList | undefined => {
  if (!oldData) return oldData;
  return {
    ...oldData,
    data: oldData.data.map(item => predicate(item) ? updater(item) : item),
  };
};

export const addToPagedList = <TList extends { data: unknown[]; totalItemCount?: number; }>(
  oldData: TList | undefined,
  newItem: TList['data'][number]
): TList | undefined => {
  if (!oldData) return oldData;
  return {
    ...oldData,
    data: [...oldData.data, newItem],
    totalItemCount: (oldData.totalItemCount ?? 0) + 1,
  };
};

export const removeFromPagedList = <TList extends { data: unknown[]; totalItemCount?: number; }>(
  oldData: TList | undefined,
  predicate: (item: TList['data'][number]) => boolean
): TList | undefined => {
  if (!oldData) return oldData;
  const filteredData = oldData.data.filter(item => !predicate(item));
  const removedCount = oldData.data.length - filteredData.length;
  return {
    ...oldData,
    data: filteredData,
    totalItemCount: oldData.totalItemCount !== undefined ? Math.max(oldData.totalItemCount - removedCount, 0) : undefined,
  };
};

export const upsertPagedList = <TList extends { data: unknown[]; totalItemCount?: number; }>(
  oldData: TList | undefined,
  newItem: TList['data'][number],
  predicate: (item: TList['data'][number]) => boolean
): TList | undefined => {
  if (!oldData) return oldData;
  const exists = oldData.data.some(predicate);
  if (exists) {
    return updatePagedList(oldData, predicate, () => newItem);
  }
  return addToPagedList(oldData, newItem);
};

export const removeFromList = <T, K extends keyof T>(
  oldData: T | undefined,
  predicate: (item: T[K] extends (infer U)[] ? U : never) => boolean,
  listKey: K
): T | undefined => {
  if (!oldData) return oldData;
  const list = oldData[listKey] as unknown[];
  return { ...oldData, [listKey]: list.filter(item => !predicate(item as T[K] extends (infer U)[] ? U : never)) };
};
